import {
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Popper,
  TextField,
  useAutocomplete,
} from '@mui/material';

import * as React from 'react';

import { Customer } from 'types/Customer';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCustomers, createCustomer } from 'services/clients/customers';
import { brandColors } from 'brandColors';
import { CustomerTag } from '../CustomerTag';
import { Create } from './Create';
import { Edit } from './edit';
import { HandleToggleEdit, Option } from './Option';
import { validateCode } from '../utility';

interface Props {
  /** Called when the user selects a customer */
  onSelect: (customer: Customer | null) => void;
  /** Called when the user clicks outside */
  onClickAway: () => void;
  /** The selected customer */
  customer?: Customer | null;
  /** Additional styles applied to the div containing the input and list */
  className?: string;
}

export const TagSearch: React.FC<Props> = (props) => {
  const { onSelect, onClickAway } = props;

  const queryClient = useQueryClient();

  const customerQuery = useQuery(['customers'], ({ signal }) => fetchCustomers({ signal }), {});

  const createCustomerMutation = useMutation(createCustomer, {
    mutationKey: ['customerTag'],
    onSuccess: () => {
      queryClient.invalidateQueries(['customers']);
    },
  });

  // Used for associating the edit Popper with the right button
  const [activeEditButton, setActiveEditButton] = React.useState<null | {
    button: HTMLButtonElement;
    customer: Customer;
  }>(null);

  // Track the selected customer making this a controlled component; use the props customer if provided
  const [selectedCustomer, setSelectedTag] = React.useState<Customer | null>(
    props.customer || null
  );

  const setTag = React.useCallback(
    (customer: Customer | null) => {
      setSelectedTag(customer);
      onSelect(customer);
    },
    [setSelectedTag, onSelect]
  );

  // Track the user entered text with any validation errors
  const [enteredValue, setEnteredValue] = React.useState<{
    value: string;
    problems: string[];
  }>({ value: '', problems: [] });

  const hasCodeProblems = enteredValue.problems.length !== 0;

  const createAndSetTag = React.useCallback(
    async (code: string) => {
      try {
        const customer = await createCustomerMutation.mutateAsync(code);
        setTag(customer);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [setTag, createCustomerMutation]
  );

  const handleChange = React.useCallback(
    (_: React.SyntheticEvent, customer: Customer | string | null) => {
      if (customer === null) {
        return;
      }

      if (typeof customer === 'string') {
        // If it's a string, we need to create the customer
        createAndSetTag(customer);
      } else {
        // Otherwise we can go directly to setting it
        setTag(customer);
      }
    },
    [createAndSetTag, setTag]
  );

  const handleInputChange = React.useCallback(
    (_: React.SyntheticEvent, value: string) => {
      const problems = validateCode(value);
      setEnteredValue({ value, problems });
    },
    [setEnteredValue]
  );

  // The magic of the mui autocomplete hook
  const ac = useAutocomplete({
    options: customerQuery.data || [],
    autoHighlight: true,
    getOptionLabel: (option) => (option as Customer).code || (option as string),
    onChange: handleChange,
    onInputChange: handleInputChange,
    freeSolo: !hasCodeProblems && customerQuery.isSuccess,
    disableListWrap: true,
    value: selectedCustomer,
    inputValue: selectedCustomer ? '' : enteredValue.value,
    open: true,
  });

  // We extract the blur event so we can call it when we want
  const { onBlur, ...restInputProps } = ac.getInputProps();
  /* eslint-disable-next-line */
    /* @ts-ignore */
  const { value, ref } = restInputProps;

  const handleClear = React.useCallback(() => {
    setTag(null);
    setEnteredValue({ value: '', problems: [] });
    ref.current.focus();
  }, [setTag, setEnteredValue, ref]);

  // Close any active edit popovers if their customer no longer exists in the list
  React.useEffect(() => {
    if (
      activeEditButton &&
      (!customerQuery.data?.find((c) => c.id === activeEditButton.customer.id) ||
        !(ac.groupedOptions as Customer[]).find((o) => o.id === activeEditButton.customer.id))
    ) {
      setActiveEditButton(null);
    }
  }, [activeEditButton, setActiveEditButton, customerQuery, ac.groupedOptions]);

  // Clear the selected customer if the query comes back and they no longer exist
  React.useEffect(() => {
    if (
      selectedCustomer &&
      // Data may be updating so we must check isFetching
      !customerQuery.isFetching &&
      customerQuery.data &&
      !customerQuery.data.find((c) => c.id === selectedCustomer.id)
    ) {
      handleClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerQuery.data]);

  // Focus on mount
  React.useEffect(() => {
    ref.current.focus();
    // eslint-disable-next-line
    }, []);

  const handleClickAway = React.useCallback(() => {
    if (!ac.popupOpen) {
      return;
    }

    if (activeEditButton) {
      setActiveEditButton(null);
      return;
    }

    onClickAway();
  }, [ac, activeEditButton, setActiveEditButton, onClickAway]);

  const handleCreate = () => {
    createAndSetTag(value as string);
  };

  let message: React.ReactNode = null;
  if (customerQuery.isError) {
    message = <h4 className="text-red-900">Could not retrieve reference tags…</h4>;
  } else if (hasCodeProblems) {
    message = enteredValue.problems.map((problem) => (
      <h4 className="text-red-900" key={problem}>
        {problem}
      </h4>
    ));
  } else {
    message = <h4>Select a Reference ID or create new</h4>;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div
        onKeyDown={ac.getRootProps().onKeyDown}
        className="relative inline-block h-10"
        style={{ zIndex: 1 }}
      >
        <div className={`bg-white rounded shadow-lg w-72 fixed ${props.className}`}>
          <TextField
            inputProps={{ ...restInputProps }}
            // eslint-disable-next-line
            InputProps={{
              startAdornment: selectedCustomer && (
                <InputAdornment position="start">
                  <CustomerTag customer={selectedCustomer} onClear={handleClear} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <>
                    {createCustomerMutation.isLoading && <CircularProgress size={12} />}
                    {createCustomerMutation.error && (
                      <h4 className="text-red-900">Could not create</h4>
                    )}
                  </>
                </InputAdornment>
              ),
            }}
            size="small"
            placeholder={selectedCustomer ? '' : 'Search in Reference IDs…'}
            disabled={createCustomerMutation.isLoading}
            className="w-72"
            sx={{
              '.MuiInputBase-root': {
                overflow: 'hidden',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                border: 'none',
                backgroundColor: brandColors.colors.gray[700],
              },
              '.MuiInputBase-root:hover': {
                border: 'none',
              },
              '.MuiInputBase-input': { width: selectedCustomer ? 10 : undefined },
              '.MuiInputAdornment-positionStart': { overflow: 'hidden', height: '100%' },
            }}
          />
          <div className="p-2 w-full text-gray-800 font-medium flex justify-between items-center">
            {message}
            {customerQuery.isLoading && <CircularProgress size={10} />}
          </div>
          {!hasCodeProblems && customerQuery.isSuccess && (
            <ul {...ac.getListboxProps()} className="max-h-32 overflow-auto">
              {(ac.groupedOptions as Customer[]).map((option, index) => {
                const handleToggleEdit: HandleToggleEdit = (e, customer) => {
                  const button = e.currentTarget;
                  setActiveEditButton((current) => {
                    if (current && current.customer === customer) {
                      return null;
                    }
                    return { button, customer };
                  });
                };
                return (
                  <Option
                    {...ac.getOptionProps({ option, index })}
                    customer={option}
                    onToggleEdit={handleToggleEdit}
                  />
                );
              })}
              {value && !(ac.groupedOptions as Customer[]).find((c) => c.code === value) && (
                <Create code={value as string} onClick={handleCreate} />
              )}
            </ul>
          )}
        </div>
        <Popper
          open={!!activeEditButton}
          anchorEl={activeEditButton && activeEditButton.button}
          className="shadow-md"
          placement="right"
          style={{ zIndex: 1300 }}
        >
          <div className="p-4 bg-white rounded shadow-lg w-72">
            {activeEditButton && (
              <Edit
                customer={activeEditButton.customer}
                onClose={() => setActiveEditButton(null)}
              />
            )}
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
TagSearch.displayName = 'CustomerTag.TagSearch';
